<template>
  <div class="todo-list">
    <div class="card-header">
      <h3>待办事项</h3>
      <el-button 
        v-if="isAdmin"
        type="primary" 
        size="small" 
        @click="showAddTodoDialog"
        class="add-todo-btn"
      >
        添加待办事项
      </el-button>
    </div>
    <div class="todo-content">
      <!-- 待办事项内容 -->
      <div class="todo-item" v-for="item in todoList" :key="item.id">
        <div class="todo-content-wrapper">
          <!-- 管理员显示选择框，普通用户不显示 -->
          <template v-if="isAdmin">
            <el-checkbox v-model="item.completed">
              <div 
                class="todo-title ellipsis-text" 
                :title="item.title"
                @click="showTodoDetail(item)"
              >
                {{ item.title }}
              </div>
            </el-checkbox>
          </template>
          <template v-else>
            <div 
              class="todo-title ellipsis-text" 
              :title="item.title"
              @click="showTodoDetail(item)"
            >
              {{ item.title }}
            </div>
          </template>
        </div>
        <div class="todo-actions">
          <span class="todo-date">{{ item.date }}</span>
          <div class="action-buttons" v-if="isAdmin">
            <el-button 
              type="primary" 
              size="small" 
              text 
              @click="editTodo(item)"
              class="edit-todo-btn"
            >
              编辑
            </el-button>
            <el-button 
              type="danger" 
              size="small" 
              text 
              @click="deleteTodo(item.id)"
              class="delete-todo-btn"
            >
              删除
            </el-button>
          </div>
        </div>
      </div>
      <div class="no-todo" v-if="todoList.length === 0">
        暂无待办事项
      </div>
    </div>

    <!-- 添加/编辑待办事项对话框 -->
    <el-dialog
      :title="todoDialogTitle"
      v-model="todoDialogVisible"
      width="600px"
      @close="handleTodoDialogClose">
      <el-form
        ref="todoFormRef"
        :model="todoForm"
        :rules="todoRules"
        label-width="80px">
        <el-form-item label="事项内容" prop="title">
          <el-input 
            v-model="todoForm.title" 
            placeholder="请输入待办事项内容" 
            type="textarea"
            :rows="3"
          />
        </el-form-item>
        
        <el-form-item label="上传文件">
          <el-upload
            class="upload-demo"
            action="#"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :file-list="todoForm.files"
            :auto-upload="false"
            list-type="picture"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.mp4,.avi,.mov,.webm"
          >
            <el-button type="primary" size="small">
              <el-icon><Plus /></el-icon>
              上传文件
            </el-button>
            <template #tip>
              <div class="el-upload__tip">
                支持 jpg, png, gif, mp4, avi, mov, webm 格式
              </div>
            </template>
          </el-upload>
        </el-form-item>
        
        <el-form-item label="截止日期" prop="date">
          <el-date-picker
            v-model="todoForm.date"
            type="date"
            placeholder="选择截止日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="todoDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleTodoSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 待办事项详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      :title="currentTodoDetail ? currentTodoDetail.title : ''"
      width="80%"
      top="5vh"
      center>
      <div class="todo-detail-content">
        <div class="detail-text">
          {{ currentTodoDetail ? currentTodoDetail.title : '' }}
        </div>
        <!-- 显示图片和视频 -->
        <div class="detail-media" v-if="currentTodoDetail && currentTodoDetail.images && currentTodoDetail.images.length > 0">
          <div class="media-grid">
            <div 
              v-for="(file, index) in currentTodoDetail.images" 
              :key="index" 
              class="media-item"
              @click="previewMedia(file)"
            >
              <img 
                v-if="isImage(file.type)" 
                :src="file.url" 
                :alt="file.name"
                class="media-preview"
              />
              <video 
                v-else-if="isVideo(file.type)"
                :src="file.url"
                class="media-preview"
                controls
              />
              <div v-else class="file-icon">
                <el-icon><Document /></el-icon>
                <span>{{ file.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 媒体预览对话框 -->
    <el-dialog
      v-model="previewDialogVisible"
      title="预览"
      width="80%"
      top="5vh"
      center>
      <div class="preview-content">
        <img 
          v-if="previewMediaType === 'image'" 
          :src="previewUrl" 
          style="max-width: 100%; max-height: 70vh;"
        />
        <video 
          v-else-if="previewMediaType === 'video'"
          :src="previewUrl"
          controls
          style="max-width: 100%; max-height: 70vh;"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Plus, Document } from '@element-plus/icons-vue'

export default {
  name: 'TodoList',
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    Plus,
    Document
  },
  data() {
    return {
      todoList: [
        {
          id: 1,
          title: '确认甲方下单信息，包括产品规格、数量、交付时间等详细要求，确保双方对订单内容理解一致',
          completed: false,
          date: '2025-10-28',
          images: [
            {
              name: 'sample-image.jpg',
              url: 'https://via.placeholder.com/150',
              type: 'image/jpeg'
            }
          ]
        },
        {
          id: 2,
          title: '安排生产计划，根据订单要求制定详细的生产排期，协调各部门资源确保按时完成生产任务',
          completed: false,
          date: '2025-10-29',
          images: []
        },
        {
          id: 3,
          title: '检查物料库存，确认原材料和零部件的库存情况，及时补充不足的物料，避免影响生产进度',
          completed: true,
          date: '2025-10-27',
          images: []
        }
      ],
      todoDialogVisible: false,
      todoDialogTitle: '添加待办事项',
      submitting: false,
      editingTodoId: null,
      previewDialogVisible: false,
      previewUrl: '',
      previewMediaType: '',
      detailDialogVisible: false,
      currentTodoDetail: null,
      todoForm: {
        title: '',
        date: '',
        files: []
      },
      todoRules: {
        title: [
          { required: true, message: '请输入待办事项内容', trigger: 'blur' },
          { min: 2, max: 500, message: '事项内容长度在 2 到 500 个字符', trigger: 'blur' }
        ],
        date: [
          { required: true, message: '请选择截止日期', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    todoFormRef() {
      return this.$refs.todoFormRef
    }
  },
  methods: {
    // 显示添加待办事项对话框
    showAddTodoDialog() {
      this.todoDialogTitle = '添加待办事项'
      this.editingTodoId = null
      this.todoForm = {
        title: '',
        date: this.getTodayDate(),
        files: []
      }
      this.todoDialogVisible = true
    },

    // 编辑待办事项
    editTodo(todo) {
      this.todoDialogTitle = '编辑待办事项'
      this.editingTodoId = todo.id
      this.todoForm = {
        title: todo.title,
        date: todo.date,
        files: todo.images || []
      }
      this.todoDialogVisible = true
    },

    // 删除待办事项
    deleteTodo(id) {
      this.$confirm('确定要删除这个待办事项吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.todoList.findIndex(item => item.id === id)
        if (index !== -1) {
          this.todoList.splice(index, 1)
          ElMessage.success('删除成功')
        }
      }).catch(() => {
        // 用户取消删除
      })
    },

    // 处理文件上传
    handleFileChange(file, fileList) {
      this.todoForm.files = fileList
    },

    // 处理文件删除
    handleFileRemove(file, fileList) {
      this.todoForm.files = fileList
    },

    // 判断是否为图片
    isImage(type) {
      return type && type.startsWith('image/')
    },

    // 判断是否为视频
    isVideo(type) {
      return type && type.startsWith('video/')
    },

    // 预览媒体文件
    previewMedia(file) {
      this.previewUrl = file.url
      this.previewMediaType = this.isImage(file.type) ? 'image' : 'video'
      this.previewDialogVisible = true
    },

    // 显示待办事项详情
    showTodoDetail(todo) {
      this.currentTodoDetail = todo
      this.detailDialogVisible = true
    },

    // 获取今天日期
    getTodayDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },

    // 处理待办事项提交
    async handleTodoSubmit() {
      if (!this.todoFormRef) return

      try {
        await this.todoFormRef.validate()
        this.submitting = true

        // 处理上传的文件
        const images = this.todoForm.files.map(file => ({
          name: file.name,
          url: file.url || URL.createObjectURL(file.raw),
          type: file.type || this.getFileType(file.name)
        }))

        if (this.editingTodoId) {
          // 编辑待办事项
          const index = this.todoList.findIndex(item => item.id === this.editingTodoId)
          if (index !== -1) {
            this.todoList[index] = {
              ...this.todoList[index],
              title: this.todoForm.title,
              date: this.todoForm.date,
              images: images
            }
          }
          ElMessage.success('编辑成功')
        } else {
          // 添加待办事项
          const newTodoItem = {
            id: Date.now(),
            title: this.todoForm.title,
            completed: false,
            date: this.todoForm.date,
            images: images
          }
          this.todoList.unshift(newTodoItem)
          ElMessage.success('添加成功')
        }

        this.todoDialogVisible = false
      } catch (error) {
        // 验证失败
      } finally {
        this.submitting = false
      }
    },

    // 获取文件类型
    getFileType(filename) {
      const ext = filename.split('.').pop().toLowerCase()
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif']
      const videoTypes = ['mp4', 'avi', 'mov', 'webm']
      
      if (imageTypes.includes(ext)) return 'image/' + (ext === 'jpg' ? 'jpeg' : ext)
      if (videoTypes.includes(ext)) return 'video/' + ext
      return 'application/octet-stream'
    },

    // 对话框关闭
    handleTodoDialogClose() {
      this.todoFormRef?.resetFields()
      this.todoForm.files = []
    }
  }
}
</script>

<style scoped>
.todo-list {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.card-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.todo-content {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

/* 滚动条样式 */
.todo-content::-webkit-scrollbar {
  width: 6px;
}

.todo-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.todo-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.todo-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.todo-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.todo-item:last-child {
  border-bottom: none;
}

.todo-content-wrapper {
  flex: 1;
  margin-right: 15px;
}

.todo-title {
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s;
}

.todo-title:hover {
  color: #409EFF;
}

/* 省略号文本样式 */
.ellipsis-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: 2.8em;
}

.todo-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 120px;
}

.todo-date {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 5px;
}

.no-todo {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 40px 0;
}

.add-todo-btn {
  white-space: nowrap;
}

.edit-todo-btn,
.delete-todo-btn {
  padding: 4px 8px;
  font-size: 12px;
}

/* 详情对话框样式 */
.todo-detail-content {
  padding: 20px 0;
}

.detail-text {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  color: #303133;
}

.detail-media {
  margin-top: 20px;
}

.media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.media-item {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e4e7ed;
  transition: all 0.3s;
}

.media-item:hover {
  border-color: #409EFF;
  transform: scale(1.05);
}

.media-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 12px;
  text-align: center;
  padding: 8px;
}

.file-icon .el-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

/* 上传组件样式 */
:deep(.el-upload-list--picture .el-upload-list__item) {
  width: 100px;
  height: 100px;
  margin-right: 8px;
  margin-bottom: 8px;
}

:deep(.el-upload-list--picture .el-upload-list__item-thumbnail) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:deep(.el-upload__tip) {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.upload-demo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

:deep(.el-upload-list) {
  display: flex;
  flex-wrap: wrap;
  margin-top: 8px;
}

.preview-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}
</style>
